import { Component, EventEmitter, forwardRef, Input, HostBinding, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ValidatorFn, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { Subscription, startWith } from 'rxjs';

import { Option } from '../../models/option.model';
import { TranslationBundle, TranslationService } from '../../../translation.service';

interface SelectOption extends Option {
  disabled?: boolean
}

@Component({
  host: {
    'test-id': 'app-select',
  },
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => AppSelectComponent), // eslint-disable-line @typescript-eslint/no-use-before-define
    multi: true,
  }],
  selector: 'app-select',
  styleUrls: ['./app-select.component.scss'],
  templateUrl: './app-select.component.html',
})

export class AppSelectComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() options: SelectOption[] = [];
  @Input() placeholder = '';
  @Input() multiple = false; // whether or not you want it to be a multiselect input
  @Input() required = false;
  @Input() selectAll = false;
  @Input() showClear = false;
  @Input() showSearch = false;
  @Input() searchPlaceholder = '';
  @Input() default = '';
  @Input() validators: ValidatorFn[] = []
  @Input() value: string | string[];

  @Output() valueChange = new EventEmitter<string | string[]>();
  @Output() blur = new EventEmitter<string | string[]>(); // emit selected values on blur

  @HostBinding('class.disabled') disabled = false

  private messages = TranslationBundle.EMPTY
  private subscriptions = {
    messages: Subscription.EMPTY,
  }

  control = new FormControl();
  filteredOptions: SelectOption[] = [];
  onChange: (value: unknown) => {}
  onTouched: () => {}
  search = new FormControl('');

  constructor(
    private translate: TranslationService,
  ) {
    this.subscriptions.messages = this.translate.getBundle('appSelect').subscribe(messages => this.messages = messages);
  }

  ngOnInit(): void {
    this.value = this.value || (this.multiple ? [] : '');
    this.validators = [];
    if (this.required) {
      this.validators.push(Validators.required);
    }
    this.control.setValidators(this.validators);
    let initialValue = this.value;
    // user specified default value
    if (this.value.length < 1 && this.default) {
      initialValue = this.default;
    }
    this.control.setValue(initialValue);
    this.valueChange.subscribe((value) => {
      this.control.setValue(value);
      this?.onChange(value);
    });
    this.search.valueChanges.pipe(startWith('')).subscribe(() => {
      this.filterOptions();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.control) {
      return;
    }
    if (this.value !== this.control.value) {
      this.control.setValue(this.value);
    }
    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }
    if (changes.options) {
      this.filterOptions();
    }
  }

  get isIndeterminate(): boolean {
    return this.value.length > 0 && this.value.length !== this.options.length;
  }

  get allOptionsSelected(): boolean {
    return this.value.length === this.options.length;
  }

  openedChange(opening: boolean): void {
    if (!opening) {
      this.blur.emit(this.control.value);
      this.search.reset('');
    }
  }

  selectOption(event: MatSelectChange): void {
    this.value = event.value;
    this.valueChange.next(this.value);
  }

  toggleSelectAll(event?: MouseEvent): void {
    event?.stopPropagation();
    this.value = !this.allOptionsSelected ? this.options.map(option => option.value) : [];
    this.valueChange.next(this.value);
  }

  filterOptions(): void {
    const search = this.search.value.toLowerCase();
    this.filteredOptions = !this.showSearch
      ? this.options
      : (this.options || []).filter((option) => {
        return (option.label || '').toLowerCase().includes(search)
          || (option.value || '').toLowerCase().includes(search);
      });
  }

  clearValue(): void {
    this.value = this.multiple ? [] : '';
    this.valueChange.next(this.value);
  }

  getDisplayText(): string {
    if (this.value.length === this.options.length) {
      return this.translate.interpolate(this.messages.get('allSelected'), this.options.length);
    } else {
      return this.options
        .filter(option => this.value.includes(option.value))
        .map(({ label }) => label)
        .join(', ');
    }
  }

  getTooltipValue(): string | null {
    // no tooltip needed if not using multi-select
    if (!this.multiple) {
      return null;
    }
    const values = [];
    for (const value of (this.value as string[])) {
      const label = this.options.find((option) => option.value === value)?.label;
      if (label) {
        values.push(label);
      }
    }
    return values.join('\n');
  }

  /** ControlValueAccessor **/
  writeValue(value: string | string[]): void {
    this.value = value;
  }

  registerOnChange(fn: (value: unknown) => {}): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
    this.control[this.disabled ? 'disable' : 'enable']();
  }
}
